import {useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";
import {parseISO} from "date-fns";

import {Container} from "@pg-design/grid-module";
import {FaqSection, IFaqQuestion} from "@pg-mono/faq";
import {priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {PricesViewType} from "../helpers/prices_view_type";
import {findExactMonth} from "../utils/find_exact_month";
import {FaqCities} from "../utils/generic_faq_cities";
import {getLatestPriceReport} from "../utils/get_latest_price_report";
import {getMedianPriceM2WithYearToYearPercent} from "../utils/get_median_price_m2";
import {getReportPeriod} from "../utils/get_report_period";
import {calculatePercent, getPercentMeaning, getPercentMoreLess, getPercentTrendVerb, getSignedPercentString} from "../utils/percents_handling_helpers";
import {useDistrictPriceExtremes} from "../utils/use_district_price_extremes";

interface Props {
    region: IRegionReportPrice;
    type: PricesViewType;
}

export const PricesRegionsFAQ = (props: Props) => {
    const {region, type} = props;
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const prices = useSelector((state: IRPStore) => state.report.price.prices);
    const extremes = useDistrictPriceExtremes(region, type);

    const latestPriceReport = getLatestPriceReport(prices);
    const yearAgoPrices = prices && latestPriceReport ? findExactMonth(prices, 12, parseISO(latestPriceReport.date)) : null;

    // Signed r/r against the year-ago price - the same formula as the trend insight, so both sections show one number.
    const avgPriceDifference = latestPriceReport && yearAgoPrices ? calculatePercent(yearAgoPrices.avg_price, latestPriceReport.avg_price) : null;
    const avgPriceM2Difference = latestPriceReport && yearAgoPrices ? calculatePercent(yearAgoPrices.avg_price_m2, latestPriceReport.avg_price_m2) : null;

    const additionalFaqs = useMemo((): IFaqQuestion[] | null => {
        const additionalFaqsArray: IFaqQuestion[] = [];
        const reportPeriod = getReportPeriod(latestPriceReport?.date);

        const {name_declension_where: regionWhere, slug: regionSlug} = region;
        const minDistrictName = extremes?.min.name || "";
        const maxDistrictName = extremes?.max.name || "";
        const minDistrictPrice = priceFormat(extremes?.min.avgPriceM2 || 0);
        const maxDistrictPrice = priceM2Format(extremes?.max.avgPriceM2 || 0);
        const latestAvgPriceM2 = priceFormat(latestPriceReport?.avg_price_m2 || 0);
        const latestAvgPrice = priceFormat(latestPriceReport?.avg_price || 0);

        /**
         * Median paragraph appended to the m2 answer, shared by `answer` and `answer_text` so they can't
         * drift apart. Empty when the median or its year-ago counterpart is missing - no "0 zł", no brackets.
         */
        const median = getMedianPriceM2WithYearToYearPercent(latestPriceReport, yearAgoPrices);
        const medianAnswerPart = median
            ? ` Mediana wynosiła ${priceFormat(median.price)} za m². To wartość środkowa, na którą nie wpływają pojedyncze bardzo drogie mieszkania. Typowa cena za metr rok do roku ${getPercentTrendVerb(
                  median.percent
              )} (${getSignedPercentString(median.percent)}).`
            : "";

        if (latestPriceReport && yearAgoPrices && avgPriceM2Difference !== null && avgPriceDifference !== null) {
            const priceM2YearToYear =
                avgPriceM2Difference === 0
                    ? "To tyle samo, co w ubiegłym roku."
                    : `To ${Math.abs(avgPriceM2Difference)}% ${getPercentMoreLess(avgPriceM2Difference)} niż w ubiegłym roku.`;
            const priceM2Answer = `W ${reportPeriod.month} ${reportPeriod.year} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. ${priceM2YearToYear}${medianAnswerPart}`;
            const priceYearToYear = avgPriceDifference === 0 ? "brak zmian" : `${getPercentMeaning(avgPriceDifference)} o ${Math.abs(avgPriceDifference)}%`;
            const priceAnswer = `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${priceYearToYear} rok do roku.`;

            additionalFaqsArray.push(
                {
                    name: `Ile kosztuje metr kwadratowy mieszkania ${regionWhere}?`,
                    answer: priceM2Answer,
                    answer_text: priceM2Answer
                },
                {
                    name: `Ile kosztuje mieszkanie ${regionWhere}?`,
                    answer: priceAnswer,
                    answer_text: priceAnswer
                }
            );
        }

        if (FaqCities.includes(regionSlug) && extremes) {
            additionalFaqsArray.push(
                {
                    name: `Która dzielnica ${regionWhere} jest najtańsza?`,
                    answer: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`,
                    answer_text: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`
                },
                {
                    name: `Jakie dzielnice są najdroższe ${regionWhere}?`,
                    answer: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`,
                    answer_text: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`
                }
            );
        }

        return additionalFaqsArray.length > 0 ? additionalFaqsArray : null;
    }, [region, extremes, latestPriceReport, yearAgoPrices, avgPriceDifference, avgPriceM2Difference]);

    return (
        <>
            {((faq && faq.length > 0) || additionalFaqs) && (
                <Container className={containerStyle}>
                    <FaqSection faqContent={faq} genericQuestions={additionalFaqs} genericFaqTitle="Najczęstsze pytania" className={faqStyle} />
                </Container>
            )}
        </>
    );
};

const containerStyle = css({
    py: 3,
    mb: 0,

    md: {
        py: 4
    }
});

const faqStyle = css({
    "& h2": {
        textAlign: "center"
    },
    md: {
        width: "100%",
        maxWidth: "100%"
    }
});
import {useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";
import {parseISO} from "date-fns";

import {Container} from "@pg-design/grid-module";
import {FaqSection, IFaqQuestion} from "@pg-mono/faq";
import {priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {PricesViewType} from "../helpers/prices_view_type";
import {findExactMonth} from "../utils/find_exact_month";
import {FaqCities} from "../utils/generic_faq_cities";
import {getLatestPriceReport} from "../utils/get_latest_price_report";
import {getMedianPriceM2WithYearToYearPercent} from "../utils/get_median_price_m2";
import {getReportPeriod} from "../utils/get_report_period";
import {calculatePercent, getPercentMeaning, getPercentMoreLess, getPercentTrendVerb, getSignedPercentString} from "../utils/percents_handling_helpers";
import {useDistrictPriceExtremes} from "../utils/use_district_price_extremes";

interface Props {
    region: IRegionReportPrice;
    type: PricesViewType;
}

export const PricesRegionsFAQ = (props: Props) => {
    const {region, type} = props;
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const prices = useSelector((state: IRPStore) => state.report.price.prices);
    const extremes = useDistrictPriceExtremes(region, type);

    const latestPriceReport = getLatestPriceReport(prices);
    const yearAgoPrices = prices && latestPriceReport ? findExactMonth(prices, 12, parseISO(latestPriceReport.date)) : null;

    // Signed r/r against the year-ago price - the same formula as the trend insight, so both sections show one number.
    const avgPriceDifference = latestPriceReport && yearAgoPrices ? calculatePercent(yearAgoPrices.avg_price, latestPriceReport.avg_price) : null;
    const avgPriceM2Difference = latestPriceReport && yearAgoPrices ? calculatePercent(yearAgoPrices.avg_price_m2, latestPriceReport.avg_price_m2) : null;

    const additionalFaqs = useMemo((): IFaqQuestion[] | null => {
        const additionalFaqsArray: IFaqQuestion[] = [];
        const reportPeriod = getReportPeriod(latestPriceReport?.date);

        const {name_declension_where: regionWhere, slug: regionSlug} = region;
        const minDistrictName = extremes?.min.name || "";
        const maxDistrictName = extremes?.max.name || "";
        const minDistrictPrice = priceFormat(extremes?.min.avgPriceM2 || 0);
        const maxDistrictPrice = priceM2Format(extremes?.max.avgPriceM2 || 0);
        const latestAvgPriceM2 = priceFormat(latestPriceReport?.avg_price_m2 || 0);
        const latestAvgPrice = priceFormat(latestPriceReport?.avg_price || 0);

        /**
         * Median paragraph appended to the m2 answer, shared by `answer` and `answer_text` so they can't
         * drift apart. Empty when the median or its year-ago counterpart is missing - no "0 zł", no brackets.
         */
        const median = getMedianPriceM2WithYearToYearPercent(latestPriceReport, yearAgoPrices);
        const medianAnswerPart = median
            ? ` Mediana wynosiła ${priceFormat(median.price)} za m². To wartość środkowa, na którą nie wpływają pojedyncze bardzo drogie mieszkania. Typowa cena za metr rok do roku ${getPercentTrendVerb(
                  median.percent
              )} (${getSignedPercentString(median.percent)}).`
            : "";

        if (latestPriceReport && yearAgoPrices && avgPriceM2Difference !== null && avgPriceDifference !== null) {
            const priceM2YearToYear =
                avgPriceM2Difference === 0
                    ? "To tyle samo, co w ubiegłym roku."
                    : `To ${Math.abs(avgPriceM2Difference)}% ${getPercentMoreLess(avgPriceM2Difference)} niż w ubiegłym roku.`;
            const priceM2Answer = `W ${reportPeriod.month} ${reportPeriod.year} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. ${priceM2YearToYear}${medianAnswerPart}`;
            const priceYearToYear = avgPriceDifference === 0 ? "brak zmian" : `${getPercentMeaning(avgPriceDifference)} o ${Math.abs(avgPriceDifference)}%`;
            const priceAnswer = `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${priceYearToYear} rok do roku.`;

            additionalFaqsArray.push(
                {
                    name: `Ile kosztuje metr kwadratowy mieszkania ${regionWhere}?`,
                    answer: priceM2Answer,
                    answer_text: priceM2Answer
                },
                {
                    name: `Ile kosztuje mieszkanie ${regionWhere}?`,
                    answer: priceAnswer,
                    answer_text: priceAnswer
                }
            );
        }

        if (FaqCities.includes(regionSlug) && extremes) {
            additionalFaqsArray.push(
                {
                    name: `Która dzielnica ${regionWhere} jest najtańsza?`,
                    answer: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`,
                    answer_text: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`
                },
                {
                    name: `Jakie dzielnice są najdroższe ${regionWhere}?`,
                    answer: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`,
                    answer_text: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`
                }
            );
        }

        return additionalFaqsArray.length > 0 ? additionalFaqsArray : null;
    }, [region, extremes, latestPriceReport, yearAgoPrices, avgPriceDifference, avgPriceM2Difference]);

    return (
        <>
            {((faq && faq.length > 0) || additionalFaqs) && (
                <Container className={containerStyle}>
                    <FaqSection faqContent={faq} genericQuestions={additionalFaqs} genericFaqTitle="Najczęstsze pytania" className={faqStyle} />
                </Container>
            )}
        </>
    );
};

const containerStyle = css({
    py: 3,
    mb: 0,

    md: {
        py: 4
    }
});

const faqStyle = css({
    "& h2": {
        textAlign: "center"
    },
    md: {
        width: "100%",
        maxWidth: "100%"
    }
});
